// ============================================================
// 請求書 API クライアント（請求一覧 / 請求詳細）
// 自BE (client-setting-backend) の /api/v1/clients/:clientId/billing[/:ym] を呼ぶ。
// 金額は確定請求テーブルを持たず、契約・料金マスターと利用集計から都度算出される。
// ============================================================

import ipponApi from "../../../config/axios";
import { toPdfBlob } from "./invoiceBlob";
import { Invoice, InvoiceLineItem, InvoiceSummary, ServiceKey } from "./types";

const BaseURL = import.meta.env.VITE_API_URL;

/** 一覧で遡る月数（BE の上限。年度フィルタの選択肢もこの範囲から作る） */
export const LIST_MONTHS = 36;

// ===== API レスポンス型（client-setting-backend と一致） =====

type InvoiceSummaryDto = Partial<InvoiceSummary>;
type InvoiceDto = Partial<Invoice>;

// ===== DTO → 画面用の型へ変換 =====

const toLineItem = (raw: Partial<InvoiceLineItem>): InvoiceLineItem => ({
  service: (raw.service ?? "property") as ServiceKey,
  cycle: raw.cycle ?? "monthly",
  name: raw.name ?? "",
  qty: raw.qty ?? 0,
  qtyUnit: raw.qtyUnit,
  unitPrice: raw.unitPrice ?? 0,
  taxRate: raw.taxRate ?? 0.1,
  amount: raw.amount ?? 0,
  note: raw.note,
  custom: raw.custom,
});

const toSummary = (raw: InvoiceSummaryDto): InvoiceSummary => ({
  id: raw.id ?? "",
  invoiceNo: raw.invoiceNo ?? "",
  year: raw.year ?? 0,
  month: raw.month ?? 0,
  issueDate: raw.issueDate ?? "",
  dueDate: raw.dueDate ?? "",
  billingCycle: raw.billingCycle ?? "monthly",
  status: raw.status ?? "billed",
  total: raw.total ?? 0,
  // 未指定（PDF有無を返さないAPI）の場合は、従来どおり表示できる前提にする
  hasPdf: raw.hasPdf ?? true,
});

const toInvoice = (raw: InvoiceDto): Invoice => ({
  ...toSummary(raw),
  client: { companyName: raw.client?.companyName ?? "" },
  issuer: {
    postalCode: raw.issuer?.postalCode ?? "",
    address: raw.issuer?.address ?? "",
    companyName: raw.issuer?.companyName ?? "",
    registrationNumber: raw.issuer?.registrationNumber ?? "",
    bankName: raw.issuer?.bankName ?? "",
    bankBranch: raw.issuer?.bankBranch ?? "",
    bankAccount: raw.issuer?.bankAccount ?? "",
    person: raw.issuer?.person ?? "",
  },
  subject: raw.subject ?? "",
  period: raw.period ?? "",
  items: (raw.items ?? []).map(toLineItem),
  subtotalExcl: raw.subtotalExcl ?? 0,
  tax: raw.tax ?? 0,
  remarks: raw.remarks,
});

// ===== API 呼び出し =====

/**
 * 請求一覧を取得する（新しい月順）。
 * 請求書はサービスごとに分けず、1ヶ月分を1通にまとめる（合算）。
 * 年度の選択肢は取得結果から作るため、既定で遡れる最大月数をまとめて取得する。
 */
export async function fetchInvoicesApi(
  clientId: string,
  months = LIST_MONTHS
): Promise<InvoiceSummary[]> {
  const res = await ipponApi.get(
    `${BaseURL}/api/v1/clients/${clientId}/billing`,
    { params: { months } }
  );
  const items = (res.data?.data?.items ?? []) as InvoiceSummaryDto[];
  return items.map(toSummary);
}

/** 請求書1通（明細つき）を取得する。ym は YYYY-MM */
export async function fetchInvoiceApi(
  clientId: string,
  ym: string
): Promise<Invoice> {
  const res = await ipponApi.get(
    `${BaseURL}/api/v1/clients/${clientId}/billing/${ym}`
  );
  return toInvoice((res.data?.data ?? {}) as InvoiceDto);
}

/**
 * 請求書PDFを取得する（BEが生成したPDFそのもの）。
 * 認証ヘッダーが必要なため、URLを直接開かずAPI経由で取得して Blob で扱う。
 *
 * Accept を application/pdf に固定すること。API Gateway (REST API) は
 * レスポンスをバイナリとして扱うかを、リクエストの Accept と
 * binaryMediaTypes の一致で判定する。axios の既定値
 * （application/json, text/plain, ＊/＊）のままだと一致せず、
 * base64 のまま復号されずに届き、PDFとして開けなくなる。
 */
export async function fetchInvoicePdfApi(
  clientId: string,
  ym: string
): Promise<Blob> {
  const res = await ipponApi.get(
    `${BaseURL}/api/v1/clients/${clientId}/billing/${ym}/pdf`,
    {
      responseType: "blob",
      headers: { Accept: "application/pdf" },
    }
  );
  // 設定差で base64 のまま届くことがあるため、ここで整えてから返す
  return toPdfBlob(res.data as Blob);
}
